<script lang="ts" setup>
import { capitalize, lowerFirst } from '@sancare/typescript-commons/src/misc/string-utils'
import { FilterDataValue, FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import { computed, onMounted, Ref, ref, watch } from 'vue'

import {
  FilterData,
  FilteredOption,
  GroupOption,
  isLocalizedOption,
  isPmsiTypedOption,
  LocalizedOption
} from '@/store/modules/filters/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const props = withDefaults(defineProps<{
  filterData: FilterData
  initialValue?: FilterDataValue | null
  isLegacy?: boolean
  pmsiCountry: string
  pmsiType: PmsiTypeEnum
}>(), {
  initialValue: null,
  isLegacy: false,
})

const emit = defineEmits<{
  (e: 'add-filter', filter: FilterEvent): void
}>()

const filterValue = ref('')
const subFiltersValues = ref({}) as Ref<{ [key: string]: string }>

const getFilteredOption = (option: string | FilteredOption | GroupOption) => {
  if (!isLocalizedOption(option) || option.country === props.pmsiCountry)
    if (!isPmsiTypedOption(option) || option.pmsiType === props.pmsiType)
      return true
  return false
}

const options = computed(() => {
  const options = props.filterData.options as Array<string | LocalizedOption | GroupOption>

  if (props.filterData.optionFiltered) {
    return options.filter(getFilteredOption)
  }

  return options.map((option) => {
    return {
      value: option,
      label: displayOptionLabel(props.filterData.labels, option),
    }
  })
})

function addFilter() {
  if (filterValue.value === '') {
    return
  }

  const name = props.filterData.name
  let type = props.filterData.format
  const value = filterValue.value

  type = type.replace('{filterType}', capitalize(props.filterData.name))
  if (props.filterData.subFilters) {
    props.filterData.subFilters.forEach((filter) => {
      type = type.replace(`{${filter.name}}`, capitalize(subFiltersValues.value[filter.name]))
    })
  }
  type = lowerFirst(type)
  emit('add-filter', { name, type, value })
}

function displayOptionLabel(labels, option) {
  return (labels && labels[option]) ? labels[option] : option
}

watch(
  () => props.initialValue,
  (val) => {
    if (!val) return
    filterValue.value = val.value
    subFiltersValues.value = val.subFilters ?? {}
  },
  { immediate: true }
)

onMounted(() => {
  if (props.initialValue) return

  filterValue.value = props.filterData.default ?? ''
  const defaultValues = {}
  if (props.filterData.subFilters) {
    props.filterData.subFilters.forEach((filter) => {
      defaultValues[filter.name] = filter.default ?? filter.options?.[0] ?? ''
    })
  }
  subFiltersValues.value = defaultValues
})
</script>

<template>
  <div class="row">
    <div
      :class="{
        'col-4': isLegacy && (!filterData.subFilters || filterData.subFilters.length < 2),
        'col-10': isLegacy && filterData.subFilters && filterData.subFilters.length >= 2,
        'col-12': !isLegacy || filterData.name.includes('documentCategory')
      }"
      class="input-group"
    >
      <select
        v-for="filter in filterData.subFilters"
        :id="`select-filter-sub-${filter.name}`"
        :key="filter.name"
        v-model="subFiltersValues[filter.name]"
        class="form-select"
      >
        <option
          v-for="opt in filter.options"
          :key="opt"
          :value="opt"
        >
          {{ filter.labels[opt] ? filter.labels[opt] : opt }}
        </option>
      </select>
      <select
        id="select-filter-value"
        v-model="filterValue"
        class="form-select"
      >
        <option
          value=""
          disabled
        >
          ---
        </option>
        <option
          v-for="opt in options"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <div class="input-group-append">
        <button
          id="select-filter-add"
          class="btn btn-primary"
          type="button"
          @click="addFilter"
        >
          Ok
        </button>
      </div>
    </div>
  </div>
</template>
